import { computed, reactive, ref } from "vue";
import { logger } from "@/infrastructure/services/LoggerService";
import { resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import { createEmptyTaxReceiptEmailContexts, TAX_RECEIPT_EMAIL_CONTEXTS, } from "../domain/entities/taxReceiptEmailContexts";
import { getTaxReceiptEmailContexts, updateTaxReceiptEmailContext, } from "../infrastructure/adapters/taxReceiptEmailContextsService";
const ERR = "finance.taxReceipts.settings.errors";
/** Backend codes → i18n keys (keep in sync with OPDON `TaxReceiptSettingsErrorCodes`, email part). */
export const TAX_RECEIPT_EMAIL_CONTEXT_ERROR_KEYS = {
    "TaxReceiptSettings.EmailContext.Required": `${ERR}.emailContext`,
    "TaxReceiptSettings.EmailContext.Invalid": `${ERR}.emailContext`,
    "TaxReceiptSettings.EmailContext.DefaultTemplate.Invalid": `${ERR}.emailDefaultTemplate`,
    // Another editor saved the same context between our read and our write.
    "TaxReceiptSettings.EmailContext.ConcurrentUpdate": `${ERR}.emailContextConcurrentUpdate`,
    "TaxReceiptSettings.EmailContext.Subject.Required": `${ERR}.emailSubject`,
    "TaxReceiptSettings.EmailContext.Subject.TooLong": `${ERR}.emailSubject`,
    "TaxReceiptSettings.EmailContext.Subject.Invalid": `${ERR}.emailSubject`,
    "TaxReceiptSettings.EmailContext.Subject.UnknownVariable": `${ERR}.emailSubjectUnknownVariable`,
    "TaxReceiptSettings.EmailContext.Subject.MalformedVariable": `${ERR}.emailSubjectMalformedVariable`,
    // The stored rules travel back unchanged on the PUT; OPDON can still refuse them.
    "TaxReceiptSettings.EmailRules.Invalid": `${ERR}.emailRules`,
    "TaxReceiptSettings.EmailRules.Filter.Invalid": `${ERR}.emailRuleFilter`,
    "TaxReceiptSettings.EmailRules.DonorTypeFilter.Required": `${ERR}.emailRuleFilter`,
    "TaxReceiptSettings.EmailRules.DonorTypeFilter.Invalid": `${ERR}.emailRuleFilter`,
    "TaxReceiptSettings.EmailRules.DonationTypeFilter.Required": `${ERR}.emailRuleFilter`,
    "TaxReceiptSettings.EmailRules.DonationTypeFilter.Invalid": `${ERR}.emailRuleFilter`,
    "TaxReceiptSettings.EmailRules.Template.Required": `${ERR}.emailRuleTemplateRequired`,
    "TaxReceiptSettings.EmailRules.UnitSendDonationTypeLocked": `${ERR}.emailRuleUnitSendLocked`,
};
const CONCURRENT_UPDATE_KEY = `${ERR}.emailContextConcurrentUpdate`;
function snapshotOf(context) {
    return JSON.stringify({ defaultTemplateId: context.defaultTemplateId, subject: context.subject, rules: context.rules });
}
/**
 * Form over the two email contexts, scoped to their default template. Each context is dirty and
 * written on its own (`PUT …/email-contexts/{context}`, a full replace that carries the stored rules
 * back unchanged), but the page saves them behind its single Save button.
 */
export function useTaxReceiptEmailContextsForm() {
    const state = reactive(createEmptyTaxReceiptEmailContexts());
    // Refs, not `let`s: the dirty computeds must re-run after a save that stores equal values.
    const snapshots = ref({
        UnitSend: snapshotOf(state.UnitSend),
        AnnualCampaign: snapshotOf(state.AnnualCampaign),
    });
    const isLoading = ref(false);
    const isSaving = ref(false);
    const loadError = ref(false);
    const saveErrorKey = ref(null);
    function isContextDirty(context) {
        return snapshotOf(state[context]) !== snapshots.value[context];
    }
    const isDirty = computed(() => TAX_RECEIPT_EMAIL_CONTEXTS.some((context) => isContextDirty(context)));
    function applySnapshot(context, next) {
        state[context] = { ...next, rules: next.rules.map((rule) => ({ ...rule })) };
        snapshots.value = { ...snapshots.value, [context]: snapshotOf(state[context]) };
        saveErrorKey.value = null;
    }
    async function load() {
        isLoading.value = true;
        loadError.value = false;
        try {
            const contexts = await getTaxReceiptEmailContexts();
            for (const context of TAX_RECEIPT_EMAIL_CONTEXTS)
                applySnapshot(context, contexts[context]);
        }
        catch (err) {
            logger.error("Failed to load the tax-receipt email contexts", err);
            loadError.value = true;
        }
        finally {
            isLoading.value = false;
        }
    }
    function setDefaultTemplate(context, templateId) {
        state[context].defaultTemplateId = templateId || null;
        saveErrorKey.value = null;
    }
    function setSubject(context, subject) {
        state[context].subject = subject;
        saveErrorKey.value = null;
    }
    function reset() {
        for (const context of TAX_RECEIPT_EMAIL_CONTEXTS) {
            const previous = JSON.parse(snapshots.value[context]);
            state[context].defaultTemplateId = previous.defaultTemplateId;
            state[context].subject = previous.subject;
            state[context].rules = previous.rules;
        }
        saveErrorKey.value = null;
    }
    // Another editor won the race on this context. OPDON has no version check (the 409 comes from the
    // unique keys during the replace), so a second Save simply lands: the default template the user
    // chose stays in place, and the toast (kept on saveErrorKey) says to save again. The baseline moves
    // to what is stored, and so do the rules: they are not edited here but the PUT carries them, so
    // keeping the ones read at page load would write them over the other editor's.
    async function refreshBaselineAfterConcurrentUpdate(context) {
        try {
            const contexts = await getTaxReceiptEmailContexts();
            snapshots.value = { ...snapshots.value, [context]: snapshotOf(contexts[context]) };
            state[context].rules = contexts[context].rules.map((rule) => ({ ...rule }));
            state[context].updatedAt = contexts[context].updatedAt;
        }
        catch (err) {
            logger.error("Failed to re-read the tax-receipt email contexts after a concurrent update", err);
        }
    }
    /** Writes the dirty contexts one by one; the first refusal stops the run and is kept on saveErrorKey. */
    async function save() {
        isSaving.value = true;
        try {
            for (const context of TAX_RECEIPT_EMAIL_CONTEXTS.filter((candidate) => isContextDirty(candidate))) {
                try {
                    const saved = await updateTaxReceiptEmailContext(context, {
                        defaultTemplateId: state[context].defaultTemplateId,
                        subject: state[context].subject,
                        rules: state[context].rules,
                    });
                    applySnapshot(context, saved);
                }
                catch (err) {
                    const key = resolveApiErrorKey(err, TAX_RECEIPT_EMAIL_CONTEXT_ERROR_KEYS, `${ERR}.emailContextsSaveFailed`);
                    if (key === CONCURRENT_UPDATE_KEY)
                        await refreshBaselineAfterConcurrentUpdate(context);
                    saveErrorKey.value = key;
                    return "failed";
                }
            }
            return "saved";
        }
        finally {
            isSaving.value = false;
        }
    }
    return {
        state,
        isDirty,
        isLoading,
        isSaving,
        loadError,
        saveErrorKey,
        isContextDirty,
        load,
        setDefaultTemplate,
        setSubject,
        reset,
        save,
    };
}
